import { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Plus, Trash, Buildings, Pencil, Image as ImageIcon, ArrowsClockwise,
  Users, ShieldCheck, User as UserIcon, CheckCircle, Hourglass,
  Crown, Prohibit, ArrowClockwise, Lock,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";

const emptyCompany = {
  name: "", contact_email: "", address: "", tax_id: "", logo_url: "",
  admin_email: "", admin_full_name: "", admin_password: "",
};
const emptyUser = {
  company_id: "", username: "", full_name: "", email: "",
  password: "", is_admin: false, is_superadmin: false,
};

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function CompanyDialog({ open, onOpenChange, mode, initial, onSubmit }) {
  const [form, setForm] = useState(emptyCompany);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...emptyCompany, ...initial } : { ...emptyCompany });
    }
  }, [open, initial]);

  const onPickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) {
      toast.error("Logo zu groß (max. 1 MB).");
      return;
    }
    try {
      const url = await fileToDataURL(f);
      setForm((s) => ({ ...s, logo_url: url }));
    } catch {
      toast.error("Logo konnte nicht geladen werden.");
    }
  };

  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Firmenname ist Pflicht"); return; }
    if (mode === "create") {
      if (!form.admin_email?.trim() || !form.admin_password) {
        toast.error("Admin-E-Mail und -Passwort sind Pflicht"); return;
      }
    }
    setBusy(true);
    try {
      await onSubmit(form);
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Aktion fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Aktion fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="company-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Neue Firma anlegen" : "Firma bearbeiten"}</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Lege eine neue Firma + Initial-Admin an."
              : "Stammdaten, Adresse und Logo dieser Firma bearbeiten."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Firmenname *</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 rounded-sm" placeholder="z. B. Schmälzle Fleischwaren GmbH"
                data-testid="company-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Kontakt-E-Mail</Label>
              <Input value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="mt-1 rounded-sm" placeholder="info@firma.de" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Steuer-Nr.</Label>
              <Input value={form.tax_id || ""} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                className="mt-1 rounded-sm" placeholder="DE123456789" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Adresse</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="mt-1 rounded-sm" placeholder="Straße, PLZ Ort" />
            </div>

            {/* Logo */}
            <div className="sm:col-span-2 border border-dashed border-zinc-300 rounded-sm p-3 bg-zinc-50">
              <Label className="text-xs uppercase tracking-wider text-zinc-600 flex items-center gap-1">
                <ImageIcon size={14} weight="bold" /> Firmenlogo
              </Label>
              <div className="flex items-center gap-3 mt-2">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="Logo"
                    className="h-16 w-auto max-w-[180px] object-contain border border-zinc-200 bg-white p-1" />
                ) : (
                  <div className="h-16 w-24 flex items-center justify-center bg-white border border-zinc-200 text-[10px] text-zinc-400">
                    kein Logo
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml"
                    onChange={onPickLogo} className="hidden" data-testid="logo-file-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                    className="rounded-sm" data-testid="logo-upload-btn">
                    Logo wählen
                  </Button>
                  {form.logo_url && (
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => setForm((s) => ({ ...s, logo_url: "" }))}
                      className="rounded-sm text-red-600 hover:text-red-800 h-6">
                      Entfernen
                    </Button>
                  )}
                  <span className="text-[10px] text-zinc-500">
                    PNG/JPG/SVG · max. 1 MB · wird ins PDF/Excel/Label eingebettet
                  </span>
                </div>
              </div>
            </div>
          </div>

          {mode === "create" && (
            <div className="border-t border-zinc-200 pt-3 mt-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Initial-Admin der Firma</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-600">Admin-E-Mail *</Label>
                  <Input value={form.admin_email || ""} onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                    className="mt-1 rounded-sm" placeholder="chef@firma.de"
                    data-testid="admin-email-input" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-600">Voller Name</Label>
                  <Input value={form.admin_full_name || ""} onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })}
                    className="mt-1 rounded-sm" placeholder="Hans Mustermann" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-zinc-600">Initial-Passwort * (min. 8 Zeichen)</Label>
                  <Input type="text" value={form.admin_password || ""} onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                    className="mt-1 rounded-sm font-mono-num"
                    data-testid="admin-password-input" />
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#002FA7] hover:bg-[#00248a]"
            data-testid="company-submit">
            {busy ? "Speichere…" : (mode === "create" ? "Firma anlegen" : "Speichern")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ open, onOpenChange, mode, initial, companies, onSubmit }) {
  const [form, setForm] = useState(emptyUser);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...emptyUser, ...initial, password: "" }
                      : { ...emptyUser, company_id: companies[0]?.id || "" });
    }
  }, [open, initial, companies]);

  const submit = async () => {
    if (!form.full_name?.trim() || !form.username?.trim() || !form.company_id) {
      toast.error("Name, Benutzername und Firma sind Pflicht"); return;
    }
    if (mode === "create" && (!form.password || form.password.length < 6)) {
      toast.error("Passwort min. 6 Zeichen"); return;
    }
    setBusy(true);
    try {
      await onSubmit(form);
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Speichern fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Neuen Benutzer anlegen" : "Benutzer bearbeiten"}</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Lege einen Benutzer in beliebiger Firma an."
              : "Du kannst den Benutzer auch in eine andere Firma verschieben."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-600">Firma *</Label>
            <Select value={form.company_id || ""} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="user-company-select">
                <SelectValue placeholder="Firma wählen" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Voller Name *</Label>
              <Input value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-1 rounded-sm" data-testid="user-fullname-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Benutzername *</Label>
              <Input value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                className="mt-1 rounded-sm font-mono-num" data-testid="user-username-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-600">E-Mail (optional, global eindeutig)</Label>
              <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 rounded-sm" placeholder="user@firma.de" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-600">
                Passwort {mode === "edit" && <span className="normal-case text-zinc-400">(leer = unverändert)</span>}
              </Label>
              <Input type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 rounded-sm font-mono-num" placeholder={mode === "edit" ? "leer lassen" : "min. 6 Zeichen"}
                data-testid="user-password-input" />
            </div>
          </div>
          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={!!form.is_admin}
                onCheckedChange={(c) => setForm((s) => ({ ...s, is_admin: !!c }))}
                data-testid="user-isadmin-checkbox" />
              <span className="text-sm">Firmen-Admin</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={!!form.is_superadmin}
                onCheckedChange={(c) => setForm((s) => ({ ...s, is_superadmin: !!c, is_admin: !!c || s.is_admin }))}
                data-testid="user-issuperadmin-checkbox" />
              <span className="text-sm">Plattform-Superadmin</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#002FA7] hover:bg-[#00248a]"
            data-testid="user-submit">
            {busy ? "Speichere…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Status-Verwaltung pro Firma (mobiloptimiert) ---
function StatusDialog({ open, onOpenChange, company, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(7);

  if (!company) return null;

  const trialEnd = company.trial_ends_at ? new Date(company.trial_ends_at) : null;
  const now = new Date();
  const daysLeft = trialEnd ? Math.ceil((trialEnd - now) / 86400000) : null;
  const trialExpired = company.is_trial && trialEnd && daysLeft <= 0;
  const isBlocked = company.is_active === false;
  const isPaid = !company.is_trial && company.plan !== "default" && !isBlocked;

  const run = async (action, params = "") => {
    setBusy(true);
    try {
      await api.post(`/admin/companies/${company.id}/${action}${params}`);
      toast.success("Status aktualisiert");
      onChanged && onChanged();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="status-dialog">
        <DialogHeader>
          <DialogTitle>Status verwalten</DialogTitle>
          <DialogDescription>
            <strong>{company.name}</strong>
            <div className="mt-1.5">
              {isBlocked ? (
                <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-red-100 text-red-800 font-bold">Gesperrt</span>
              ) : trialExpired ? (
                <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-red-100 text-red-800 font-bold">Trial abgelaufen</span>
              ) : company.is_trial ? (
                <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-amber-100 text-amber-900 font-bold">
                  Trial · {daysLeft != null ? `${daysLeft} Tage` : "—"}
                </span>
              ) : isPaid ? (
                <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-emerald-100 text-emerald-800 font-bold">Team-Plan · Aktiv</span>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {/* Als zahlenden Kunden aktivieren */}
          {!isPaid && !isBlocked && (
            <button
              onClick={() => run("activate")}
              disabled={busy}
              className="w-full flex items-start gap-3 p-3.5 border border-zinc-200 rounded-sm hover:bg-emerald-50 hover:border-emerald-400 text-left disabled:opacity-50 disabled:hover:bg-transparent"
              data-testid="status-activate">
              <Crown size={20} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-sm">Team-Plan aktivieren</div>
                <div className="text-xs text-zinc-500 mt-0.5">Trial beenden — Firma wird zahlender Kunde</div>
              </div>
            </button>
          )}

          {/* Trial verlängern (auch wenn Trial aktiv oder bereits abgelaufen) */}
          {!isPaid && !isBlocked && (
            <div className="border border-zinc-200 rounded-sm p-3.5">
              <div className="flex items-start gap-3 mb-2">
                <Hourglass size={20} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-sm">Trial verlängern</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Testphase um X Tage erweitern</div>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  type="number" min={1} max={90}
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value || "0", 10))}
                  className="rounded-sm h-9 flex-1 font-mono-num"
                  data-testid="status-extend-days"
                />
                <Button
                  onClick={() => run("extend-trial", `?days=${days}`)}
                  disabled={busy || !days || days < 1 || days > 90}
                  className="rounded-sm h-9 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                  data-testid="status-extend">
                  +{days || 0} Tage
                </Button>
              </div>
            </div>
          )}

          {/* Zurück auf Trial */}
          {isPaid && (
            <button
              onClick={() => {
                if (!confirm(`„${company.name}" zurück auf Trial setzen? (7 Tage)`)) return;
                run("downgrade", "?days=7");
              }}
              disabled={busy}
              className="w-full flex items-start gap-3 p-3.5 border border-zinc-200 rounded-sm hover:bg-amber-50 hover:border-amber-400 text-left disabled:opacity-50"
              data-testid="status-downgrade">
              <ArrowClockwise size={20} weight="bold" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-sm">Zurück auf Trial setzen</div>
                <div className="text-xs text-zinc-500 mt-0.5">Setzt die Firma zurück in den Testmodus (7 Tage)</div>
              </div>
            </button>
          )}

          {/* Sperren / Entsperren */}
          {isBlocked ? (
            <button
              onClick={() => run("unblock")}
              disabled={busy}
              className="w-full flex items-start gap-3 p-3.5 border border-zinc-200 rounded-sm hover:bg-emerald-50 hover:border-emerald-400 text-left disabled:opacity-50"
              data-testid="status-unblock">
              <CheckCircle size={20} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-sm">Sperre aufheben</div>
                <div className="text-xs text-zinc-500 mt-0.5">Firma wieder freischalten</div>
              </div>
            </button>
          ) : (
            <button
              onClick={() => {
                if (!confirm(`„${company.name}" sperren? Benutzer können sich nicht mehr anmelden.`)) return;
                run("block");
              }}
              disabled={busy}
              className="w-full flex items-start gap-3 p-3.5 border border-zinc-200 rounded-sm hover:bg-red-50 hover:border-red-400 text-left disabled:opacity-50"
              data-testid="status-block">
              <Prohibit size={20} weight="bold" className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-sm">Firma sperren</div>
                <div className="text-xs text-zinc-500 mt-0.5">Sofortiger Zugangsstopp (Daten bleiben erhalten)</div>
              </div>
            </button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Companies() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [companyDialog, setCompanyDialog] = useState({ open: false, mode: "create", initial: null });
  const [userDialog, setUserDialog] = useState({ open: false, mode: "create", initial: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, company: null });
  const [filterCompany, setFilterCompany] = useState("all");

  const load = useCallback(async () => {
    try {
      const [{ data: comps }, { data: users }] = await Promise.all([
        api.get("/admin/companies"),
        api.get("/admin/all-users"),
      ]);
      setCompanies(comps);
      setAllUsers(users);
    } catch (err) {
      console.error("Companies load failed:", err);
      toast.error("Daten konnten nicht geladen werden");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && !user.is_superadmin) return <Navigate to="/" replace />;

  const createCompany = async (form) => {
    await api.post("/admin/companies", {
      company: {
        name: form.name.trim(),
        contact_email: form.contact_email?.trim() || null,
        address: form.address?.trim() || null,
        tax_id: form.tax_id?.trim() || null,
        logo_url: form.logo_url || null,
      },
      admin_email: form.admin_email.trim().toLowerCase(),
      admin_full_name: form.admin_full_name?.trim() || form.admin_email,
      admin_password: form.admin_password,
    });
    toast.success(`Firma „${form.name}" angelegt`);
    load();
  };

  const updateCompany = async (form) => {
    await api.put(`/admin/companies/${companyDialog.initial.id}`, {
      name: form.name.trim(),
      contact_email: form.contact_email?.trim() || null,
      address: form.address?.trim() || null,
      tax_id: form.tax_id?.trim() || null,
      logo_url: form.logo_url || null,
    });
    toast.success("Firma aktualisiert");
    load();
  };

  const removeCompany = async (c) => {
    if (!confirm(`Firma „${c.name}" UND ALLE zugehörigen Daten unwiderruflich löschen?`)) return;
    try {
      await api.delete(`/admin/companies/${c.id}`);
      toast.success("Firma gelöscht");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Löschen fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Löschen fehlgeschlagen");
    }
  };

  const createUser = async (form) => {
    await api.post("/admin/all-users", {
      company_id: form.company_id,
      username: form.username.trim().toLowerCase(),
      full_name: form.full_name.trim(),
      email: form.email?.trim() || null,
      password: form.password,
      is_admin: !!form.is_admin,
      is_superadmin: !!form.is_superadmin,
    });
    toast.success("Benutzer angelegt");
    load();
  };

  const updateUser = async (form) => {
    const payload = {
      company_id: form.company_id,
      username: form.username.trim().toLowerCase(),
      full_name: form.full_name.trim(),
      email: form.email?.trim() || null,
      is_admin: !!form.is_admin,
      is_superadmin: !!form.is_superadmin,
    };
    if (form.password) payload.password = form.password;
    await api.put(`/admin/all-users/${userDialog.initial.id}`, payload);
    toast.success("Benutzer aktualisiert");
    load();
  };

  const removeUser = async (u) => {
    if (!confirm(`Benutzer „${u.full_name}" unwiderruflich löschen?`)) return;
    try {
      await api.delete(`/admin/all-users/${u.id}`);
      toast.success("Benutzer gelöscht");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Löschen fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Löschen fehlgeschlagen");
    }
  };

  const filteredUsers = filterCompany === "all"
    ? allUsers
    : allUsers.filter((u) => u.company_id === filterCompany);

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Plattform-Superadmin</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Buildings size={28} weight="bold" />
            Firmen &amp; Benutzer
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Mandanten verwalten, Logos hochladen, Benutzer firmenübergreifend zuordnen.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-sm" data-testid="reload-btn">
          <ArrowsClockwise size={14} className="mr-1.5" /> Aktualisieren
        </Button>
      </div>

      <Tabs defaultValue="companies" className="w-full">
        <TabsList className="bg-zinc-100 p-0.5 rounded-sm">
          <TabsTrigger value="companies" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-companies">
            <Buildings size={14} className="mr-1.5" /> Firmen ({companies.length})
          </TabsTrigger>
          <TabsTrigger value="users" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-users">
            <Users size={14} className="mr-1.5" /> Alle Benutzer ({allUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* ----- Firmen-Tab ----- */}
        <TabsContent value="companies" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setCompanyDialog({ open: true, mode: "create", initial: null })}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00248a]" data-testid="add-company-button">
              <Plus weight="bold" className="mr-1.5" size={16} />Neue Firma anlegen
            </Button>
          </div>
          <div className="border border-zinc-200 rounded-sm bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-zinc-600">
                  <th className="px-5 py-3 font-medium">Firma</th>
                  <th className="px-5 py-3 font-medium">Logo</th>
                  <th className="px-5 py-3 font-medium">Plan / Status</th>
                  <th className="px-5 py-3 font-medium">Kontakt</th>
                  <th className="px-5 py-3 font-medium text-right">Benutzer</th>
                  <th className="px-5 py-3 font-medium text-right">Rezepte</th>
                  <th className="px-5 py-3 font-medium w-32"></th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => {
                  const trialEnd = c.trial_ends_at ? new Date(c.trial_ends_at) : null;
                  const now = new Date();
                  const daysLeft = trialEnd ? Math.ceil((trialEnd - now) / 86400000) : null;
                  const trialExpired = c.is_trial && trialEnd && daysLeft <= 0;
                  return (
                  <tr key={c.id} className="border-t border-zinc-100" data-testid={`company-row-${c.slug}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium">{c.name}</div>
                      <div className="text-[11px] text-zinc-500">{c.slug}{c.tax_id ? ` · ${c.tax_id}` : ""}</div>
                    </td>
                    <td className="px-5 py-3">
                      {c.logo_url ? (
                        <img src={c.logo_url} alt="Logo" className="h-8 w-auto max-w-[80px] object-contain" />
                      ) : <span className="text-[11px] text-zinc-400">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      {c.is_active === false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-red-100 text-red-800 font-bold">
                          <Lock size={10} weight="fill" /> Gesperrt
                        </span>
                      ) : trialExpired ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-red-100 text-red-800 font-bold">Trial abgelaufen</span>
                      ) : c.is_trial ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-amber-100 text-amber-900 font-bold">
                          Trial · {daysLeft != null ? `${daysLeft} Tage` : "—"}
                        </span>
                      ) : c.plan === "default" ? (
                        <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-zinc-100 text-zinc-700">Eigene Firma</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-emerald-100 text-emerald-800 font-bold">Team-Plan · Aktiv</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-zinc-600">
                      <div>{c.contact_email || "—"}</div>
                      {c.address && <div className="text-[11px] text-zinc-400">{c.address}</div>}
                    </td>
                    <td className="px-5 py-3 text-right font-mono-num">{c.user_count}</td>
                    <td className="px-5 py-3 text-right font-mono-num">{c.recipe_count}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        {c.slug !== "default" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setStatusDialog({ open: true, company: c })}
                            className="rounded-sm h-8 text-xs border-zinc-300"
                            data-testid={`status-btn-${c.slug}`}>
                            <Crown size={12} weight="fill" className="mr-1.5" />
                            Status
                          </Button>
                        )}
                        <button onClick={() => setCompanyDialog({ open: true, mode: "edit", initial: c })}
                          className="text-zinc-600 hover:text-[#002FA7] p-2 rounded-sm hover:bg-zinc-100 min-w-[36px] min-h-[36px] flex items-center justify-center"
                          data-testid={`edit-company-${c.slug}`} title="Bearbeiten">
                          <Pencil size={14} weight="bold" />
                        </button>
                        {c.slug !== "default" && (
                          <button onClick={() => removeCompany(c)}
                            className="text-red-600 hover:text-red-800 p-2 rounded-sm hover:bg-red-50 min-w-[36px] min-h-[36px] flex items-center justify-center"
                            data-testid={`delete-company-${c.slug}`} title="Löschen">
                            <Trash size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {companies.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-10 text-center text-zinc-500">Keine Firmen vorhanden.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ----- Benutzer-Tab ----- */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Filter:</Label>
              <Select value={filterCompany} onValueChange={setFilterCompany}>
                <SelectTrigger className="rounded-sm w-[260px]" data-testid="user-filter-company">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Firmen</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setUserDialog({ open: true, mode: "create", initial: null })}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00248a]" data-testid="add-user-button">
              <Plus weight="bold" className="mr-1.5" size={16} />Neuer Benutzer
            </Button>
          </div>
          <div className="border border-zinc-200 rounded-sm bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-zinc-600">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Firma</th>
                  <th className="px-5 py-3 font-medium">E-Mail</th>
                  <th className="px-5 py-3 font-medium">Benutzername</th>
                  <th className="px-5 py-3 font-medium">Rolle</th>
                  <th className="px-5 py-3 font-medium w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 font-medium">{u.full_name}</td>
                    <td className="px-5 py-3">
                      <span className="text-[11px] uppercase tracking-wider text-zinc-600 bg-zinc-100 px-2 py-1 rounded-sm">
                        {u.company_name || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-zinc-600">{u.email || "—"}</td>
                    <td className="px-5 py-3 text-zinc-600 font-mono-num">@{u.username}</td>
                    <td className="px-5 py-3">
                      {u.is_superadmin ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-amber-500 text-white">
                          <ShieldCheck size={10} weight="fill" /> Superadmin
                        </span>
                      ) : u.is_admin ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-[#002FA7] text-white">
                          <ShieldCheck size={10} weight="fill" /> Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-zinc-100 text-zinc-600 border border-zinc-200">
                          <UserIcon size={10} /> Benutzer
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setUserDialog({ open: true, mode: "edit", initial: u })}
                          className="text-zinc-600 hover:text-[#002FA7] p-1.5 rounded-sm hover:bg-zinc-100"
                          data-testid={`edit-user-${u.id}`}>
                          <Pencil size={14} weight="bold" />
                        </button>
                        <button onClick={() => removeUser(u)}
                          disabled={u.id === user?.id}
                          className="text-red-600 hover:text-red-800 p-1.5 rounded-sm hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent"
                          data-testid={`delete-user-${u.id}`}>
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-10 text-center text-zinc-500">Keine Benutzer vorhanden.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <CompanyDialog
        open={companyDialog.open}
        onOpenChange={(o) => setCompanyDialog((s) => ({ ...s, open: o }))}
        mode={companyDialog.mode}
        initial={companyDialog.initial}
        onSubmit={companyDialog.mode === "create" ? createCompany : updateCompany}
      />
      <UserDialog
        open={userDialog.open}
        onOpenChange={(o) => setUserDialog((s) => ({ ...s, open: o }))}
        mode={userDialog.mode}
        initial={userDialog.initial}
        companies={companies}
        onSubmit={userDialog.mode === "create" ? createUser : updateUser}
      />
      <StatusDialog
        open={statusDialog.open}
        onOpenChange={(o) => setStatusDialog((s) => ({ ...s, open: o }))}
        company={statusDialog.company}
        onChanged={load}
      />
    </div>
  );
}
